import { useEffect, useRef, useCallback } from 'react';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { forceRefresh, clearClientSession } from '../config/api';
import { getChatKey } from '../utils/bots';
import { aplicarEstadoEntrega, eventoEsDelChat } from '../utils/estadoEntrega';

// Detectar si estamos en desarrollo
const isDevelopment = process.env.NODE_ENV === 'development';

// Aplica un evento de media ({mediaFileId, estado, fileName, mimeType, fileSize})
// a los mensajes de un chat. Devuelve el mismo objeto si nada coincide.
export function aplicarEventoMedia(chat, evento) {
  if (!chat || !chat.messages) return chat;
  if (!chat.messages.some(m => m.mediaFileId === evento.mediaFileId)) return chat;
  return {
    ...chat,
    messages: chat.messages.map(m => {
      if (m.mediaFileId !== evento.mediaFileId) return m;
      return {
        ...m,
        mediaEstado: evento.estado,
        fileName: evento.fileName || m.fileName,
        mimeType: evento.mimeType || m.mimeType,
        fileSize: evento.fileSize || m.fileSize,
      };
    }),
  };
}

// Devuelve el mismo array si ningún chat cambia, para no repintar la lista por
// un archivo de una conversación que no está cargada.
export function aplicarEventoMediaAChats(chats, evento) {
  let cambiado = false;
  const siguientes = chats.map(chat => {
    const actualizado = aplicarEventoMedia(chat, evento);
    if (actualizado !== chat) cambiado = true;
    return actualizado;
  });
  return cambiado ? siguientes : chats;
}

// Conexión WebSocket/STOMP del Dashboard. Sus callbacks son el tercer escritor
// de `chats` y `selectedChat`, por eso recibe por parámetro las refs y los
// setters que viven en el contenedor (o en useChats) y no crea estado propio.
// No devuelve nada: el original no exponía ningún estado de conexión.
function useStompConnection({
  selectedBotIdRef,
  selectedChatRef,
  setSelectedChat,
  setChats,
  accessibleBots,
  scheduleReloadChats,
  reloadChatsFromBackend,
  markChatAsRead,
  navigate,
}) {
  // OPTIMIZACIÓN: Set para tracking de mensajes procesados (evita duplicados)
  const processedMessages = useRef(new Set());

  // OPTIMIZACIÓN: Referencias para reconexión inteligente
  const stompClientRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const connectingRef = useRef(false);
  const connectionGenerationRef = useRef(0);
  const maxReconnectAttempts = 7;
  const baseReconnectDelay = 2000; // 2 segundo base

  // 🔐 Suscripciones WebSocket POR TENANT (bot). Nos suscribimos a
  // /topic/bots.{botId}.<sufijo> por cada bot accesible en vez de a topics globales,
  // para no recibir datos de tenants ajenos. Estos refs permiten re-suscribir de
  // forma idempotente cuando cambian los bots accesibles o al reconectar.
  const subscribedBotIdsRef = useRef(new Set());
  const subscribeToBotRef = useRef(null);
  const accessibleBotsRef = useRef([]);

  // OPTIMIZACIÓN: Función para conectar WebSocket con reconexión inteligente
  const connectWebSocket = useCallback(async () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    const generation = connectionGenerationRef.current;
    try {
      console.log(`🔄 Intentando conectar WebSocket (intento ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);

      // La autenticación va por la cookie httpOnly `accessToken`, que el
      // navegador manda sola en el handshake HTTP del WebSocket; el backend la
      // captura ahí (WebSocketCookieHandshakeInterceptor) y valida el CONNECT.
      // JavaScript ya no lee el token: no hay nada que poner en connectHeaders.
      if (generation !== connectionGenerationRef.current) {
        return;
      }

      const anterior = stompClientRef.current;
      stompClientRef.current = null;
      if (anterior && typeof anterior.deactivate === 'function') {
        anterior.deactivate();
      }
      
      // Construir URL correcta para WebSocket
      const wsUrl = isDevelopment 
        ? 'http://localhost:8080/ws'
        : `${window.location.origin}/ws`;
      
      console.log('🔗 Conectando WebSocket a:', wsUrl);
      
      const socket = new SockJS(wsUrl);
      const stompClient = Stomp.over(socket);
      
      // Debug STOMP solo en desarrollo (en producción es ruido y puede exponer
      // detalles del protocolo/headers en la consola).
      stompClient.debug = isDevelopment
        ? (str) => { console.log('🐛 STOMP Debug:', str); }
        : () => {};
      
      // Configuración de reconexión y heartbeats
      stompClient.reconnectDelay = 0; // Deshabilitar reconexión automática de STOMP
      stompClient.heartbeatIncoming = 4000; // 4 segundos
      stompClient.heartbeatOutgoing = 4000; // 4 segundos
      
      // Configurar callbacks
      stompClient.onConnect = (frame) => {
        if (stompClientRef.current !== stompClient) return;
        console.log('✅ WebSocket conectado exitosamente');
        reconnectAttempts.current = 0; // Resetear contador de intentos
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
        
        // 🔐 Suscripciones POR TENANT (bot): el backend publica cada evento en
        // /topic/bots.{botId}.<sufijo> y el interceptor rechaza suscripciones a bots sin
        // acceso, evitando fugas de datos entre tenants. Nos suscribimos a un topic por
        // cada bot accesible (idempotente vía subscribedBotIdsRef).
        //
        // El separador es un punto, no una barra: con el relay STOMP de RabbitMQ
        // activado, un destino con barras se rechaza y el broker cierra la sesión,
        // así que el cliente reconecta en bucle sin recibir nada.
        subscribedBotIdsRef.current = new Set();
        const subscribeToBot = (botId) => {
          if (botId == null || subscribedBotIdsRef.current.has(botId)) return;
          subscribedBotIdsRef.current.add(botId);

        // FASE 2: WebSocket optimizado para actualizaciones de chats
        stompClient.subscribe(`/topic/bots.${botId}.chats`, (message) => {
          const updatedChat = JSON.parse(message.body);
          console.log('📨 Chat actualizado recibido:', updatedChat);
          console.log('[topic/chats] selectedBotIdRef:', selectedBotIdRef.current, 'updatedChatKey:', getChatKey(updatedChat));

          // Ignorar actualizaciones de chats que pertenezcan a otro bot: si
          // seguimos, se dispararía un reloadChatsFromBackend "por nada" y,
          // ante race conditions con el cambio de bot, podría llegar a mezclar
          // chats de bots distintos en la lista.
          if (
            selectedBotIdRef.current &&
            updatedChat.botId != null &&
            Number(updatedChat.botId) !== Number(selectedBotIdRef.current)
          ) {
            console.log(`⚠️ Chat ignorado - pertenece a bot ${updatedChat.botId}, bot seleccionado: ${selectedBotIdRef.current}`);
            return;
          }

          // Recargar chats desde el backend para mantener el orden correcto (debounced)
          scheduleReloadChats(true); // preserveMessages = true para mantener mensajes cargados

          // También actualizar selectedChat si es el chat actual
        const isSelectedChat = selectedChatRef.current &&
          getChatKey(updatedChat) === getChatKey(selectedChatRef.current);
          console.log('[topic/chats] isSelectedChat:', isSelectedChat, 'selectedChatKey:', selectedChatRef.current ? getChatKey(selectedChatRef.current) : null);
          if (isSelectedChat) {
            setSelectedChat(prev => {
              // No pisar el array `messages` del selectedChat con la versión "pelada"
              // que viene en /topic/chats (sin senderUsername, dbId, timestamp, botId, etc.).
              // Los mensajes individuales ya se gestionan por /topic/messages, que sí
              // trae todos los campos. Aquí solo actualizamos metadata del chat.
              const { messages: _ignoredMessages, ...chatMetadata } = updatedChat;
              return {
                ...prev,
                ...chatMetadata,
                unread: 0,
                avatar: updatedChat.platform === 'telegram' ? 'TG' : updatedChat.platform === 'instagram' ? 'IG' : 'WA'
              };
            });
          }
        });

        // Eventos de atención al cliente (captura/cierre) por tenant. El backend ya
        // publica aquí en push (eventos raros, no ráfagas), así que reflejamos el
        // cambio en tiempo real sin polling extra. Reenviamos el evento por el bus de
        // ventana para que la bandeja de soporte (si está abierta) pueda refrescar,
        // y refrescamos el badge de la cabecera solo si afecta al chat abierto.
        stompClient.subscribe(`/topic/bots.${botId}.support`, (message) => {
          let supportEvent;
          try {
            supportEvent = JSON.parse(message.body);
          } catch (_) {
            return;
          }
          if (!supportEvent) return;
          window.dispatchEvent(new CustomEvent('support-event', { detail: supportEvent }));
        });

        // Fin de descarga de un archivo entrante: actualiza el estado (y metadatos)
        // de los mensajes con ese mediaFileId en el chat abierto y en la lista.
        stompClient.subscribe(`/topic/bots.${botId}.media`, (message) => {
          let evento;
          try {
            evento = JSON.parse(message.body);
          } catch (_) {
            return;
          }
          if (!evento || !evento.mediaFileId) return;
          // Igual que los ticks: un evento de otro bot no es de los chats cargados.
          if (
            selectedBotIdRef.current &&
            evento.botId != null &&
            Number(evento.botId) !== Number(selectedBotIdRef.current)
          ) {
            return;
          }
          setChats(prevChats => aplicarEventoMediaAChats(prevChats, evento));
          setSelectedChat(prevSelectedChat => aplicarEventoMedia(prevSelectedChat, evento));
        });

        // Suscribirse a los mensajes nuevos del bot (topic por tenant)
        stompClient.subscribe(`/topic/bots.${botId}.messages`, (message) => {
          const newMessage = JSON.parse(message.body);
          console.log('💬 Mensaje nuevo recibido:', newMessage);
          
          // ✅ CRÍTICO: Filtrar mensajes por botId para evitar mezclar mensajes de diferentes bots
          // Solo procesar si el mensaje pertenece al bot seleccionado
          if (selectedBotIdRef.current && newMessage.botId && newMessage.botId !== selectedBotIdRef.current) {
            console.log(`⚠️ Mensaje ignorado - pertenece a bot ${newMessage.botId}, bot seleccionado: ${selectedBotIdRef.current}`);
            return; // Ignorar mensajes de otros bots
          }
          
          // OPTIMIZACIÓN: Generar clave única para el mensaje
        const messageKey = `${newMessage.platform}_${newMessage.messageId}_${newMessage.chatId}_${newMessage.botId ?? 'no-bot'}`;
          
          // OPTIMIZACIÓN: Verificar duplicados ANTES de procesar (más eficiente)
          if (processedMessages.current.has(messageKey)) {
            console.log('⚠️ Mensaje duplicado detectado en frontend, ignorando:', messageKey);
            return; // Salir temprano sin procesar
          }
          
          // Marcar mensaje como procesado
          processedMessages.current.add(messageKey);
          
          // Limpiar cache si se llena demasiado (optimización de memoria)
          // borra el 50% y mantiene las ultimas 500
          if (processedMessages.current.size > 1000) {
            console.log('🧹 Limpiando cache de mensajes procesados (modo LRU)');
            const keys = Array.from(processedMessages.current);
            keys.slice(0, 500).forEach(key => processedMessages.current.delete(key));
          }
          
          // Convertir mensaje al formato del frontend
          const formattedMessage = {
            id: newMessage.messageId,
            dbId: newMessage.dbId,
            text: newMessage.text,
            sender: newMessage.isFromBot ? "agent" : "client",
            time: newMessage.timestamp || newMessage.createdAt,
            timestamp: newMessage.timestamp,
            createdAt: newMessage.createdAt,
            platform: newMessage.platform,
            buttons: newMessage.buttons || null, // Agregar botones si existen
            senderUsername: newMessage.senderUsername || null, // ✅ Incluir username del usuario que envió el mensaje
            botId: newMessage.botId || null, // ✅ Incluir botId para mantener consistencia
            deliveryStatus: newMessage.deliveryStatus || null,
            mediaFileId: newMessage.mediaFileId || null,
            mediaEstado: newMessage.mediaEstado || null,
            messageType: newMessage.messageType || null,
            fileName: newMessage.fileName || null,
            mimeType: newMessage.mimeType || null,
            fileSize: newMessage.fileSize || null,
            mediaAntigua: !!newMessage.mediaAntigua
          };
          
          
          // Recargar chats desde el backend para mantener el orden correcto (debounced)
          // Esto garantiza que el orden siempre sea correcto según el backend
          scheduleReloadChats(true); // preserveMessages = true para mantener mensajes ya cargados
          
          // Actualizar el chat específico en la lista localmente para respuesta inmediata
          setChats(prevChats => {
            return prevChats.map(chat => {
              // ✅ CRÍTICO: Verificar chatId Y botId para evitar mezclar chats de diferentes bots
              // Solo actualizar si ambos chatId y botId coinciden
              const chatIdMatches = String(chat.chatId) === String(newMessage.chatId);
              const botIdMatches = newMessage.botId && chat.botId 
                ? Number(chat.botId) === Number(newMessage.botId)
                : !newMessage.botId && !chat.botId; // Si ambos son null/undefined, permitir (compatibilidad)
              
              if (chatIdMatches && botIdMatches) {
                console.log(`✅ Actualizando chat ${chat.name} con nuevo mensaje (botId: ${chat.botId})`);
                
                // ✅ CRÍTICO: Verificar que el chat seleccionado coincide tanto en chatId como botId
                const isSelectedChat = selectedChatRef.current && 
                  String(chat.chatId) === String(selectedChatRef.current.chatId) &&
                  (!selectedChatRef.current.botId || !chat.botId || 
                   Number(selectedChatRef.current.botId) === Number(chat.botId));
                
                if (isSelectedChat) {
                  console.log('🔄 Mensaje llegó al chat seleccionado, marcando como leído automáticamente');
                  // ✅ CRÍTICO: Pasar botId al marcar como leído
                  const chatBotId = chat.botId || newMessage.botId;
                  markChatAsRead(chat.chatId, newMessage.platform, chatBotId);
                }
                
                return {
                  ...chat,
                  messages: [...(chat.messages || []), formattedMessage],
                  lastMessage: newMessage.text,
                  unread: newMessage.isFromBot ? chat.unread : 
                          (String(chat.chatId) === String(selectedChatRef.current?.chatId) ? 0 : (chat.unread || 0) + 1),
                  avatar: chat.platform === 'telegram' ? 'TG' : chat.platform === 'instagram' ? 'IG' : 'WA'
                };
              }
              return chat;
            });
          });
          
          // Actualizar selectedChat si es el chat actual
          setSelectedChat(prevSelectedChat => {
            // ✅ CRÍTICO: Verificar tanto chatId como botId de forma estricta
            if (prevSelectedChat) {
              const chatIdMatches = prevSelectedChat.chatId === newMessage.chatId;
              const botIdMatches = newMessage.botId && prevSelectedChat.botId
                ? Number(prevSelectedChat.botId) === Number(newMessage.botId)
                : !newMessage.botId && !prevSelectedChat.botId; // Si ambos son null/undefined, permitir (compatibilidad)
              
              if (chatIdMatches && botIdMatches) {
                // OPTIMIZACIÓN: Verificación redundante eliminada (ya se hizo arriba)
                // const messageExists = prevSelectedChat.messages?.some(msg => msg.id === formattedMessage.id);
                // if (messageExists) {
                //   console.log('⚠️ Mensaje duplicado detectado en selectedChat, ignorando:', formattedMessage.id);
                //   return prevSelectedChat;
                // }
                
                // Si es un mensaje del bot, reemplazar mensajes temporales con el mismo texto
                let updatedMessages = [...(prevSelectedChat.messages || [])];
                if (newMessage.isFromBot) {
                  updatedMessages = updatedMessages.filter(msg => 
                    !(msg.isTemporary && msg.text === newMessage.text)
                  );
                }
                
                const updatedChat = {
                  ...prevSelectedChat,
                  messages: [...updatedMessages, formattedMessage],
                  lastMessage: newMessage.text,
                  unread: newMessage.isFromBot ? 0 : 
                          (String(prevSelectedChat.chatId) === String(selectedChatRef.current?.chatId) ? 0 : (prevSelectedChat.unread || 0) + 1),
                  avatar: prevSelectedChat.platform === 'telegram' ? 'TG' : prevSelectedChat.platform === 'instagram' ? 'IG' : 'WA'
                };
                              
                return updatedChat;
              }
            }
            return prevSelectedChat;
          });
        });

        // Cambios de estado de entrega (ticks) de los salientes del bot. Se reenvían
        // por el bus de ventana para la bandeja de atención y se aplican a los
        // mensajes ya cargados con la regla monótona de utils/estadoEntrega.
        stompClient.subscribe(`/topic/bots.${botId}.message-status`, (message) => {
          let estado;
          try {
            estado = JSON.parse(message.body);
          } catch (_) {
            return;
          }
          if (!estado) return;
          window.dispatchEvent(new CustomEvent('message-status', { detail: estado }));

          if (
            selectedBotIdRef.current &&
            estado.botId != null &&
            Number(estado.botId) !== Number(selectedBotIdRef.current)
          ) {
            return;
          }

          // Solo el chat del evento: no se recorren los mensajes de todos los chats
          // cargados por cada tick.
          setChats(prevChats => {
            let cambiado = false;
            const siguientes = prevChats.map(chat => {
              if (!eventoEsDelChat(chat, estado)) return chat;
              const mensajes = aplicarEstadoEntrega(chat.messages, estado);
              if (mensajes === chat.messages) return chat;
              cambiado = true;
              return { ...chat, messages: mensajes };
            });
            return cambiado ? siguientes : prevChats;
          });
          setSelectedChat(prevSelectedChat => {
            if (!eventoEsDelChat(prevSelectedChat, estado)) return prevSelectedChat;
            const mensajes = aplicarEstadoEntrega(prevSelectedChat.messages, estado);
            return mensajes === prevSelectedChat.messages
              ? prevSelectedChat
              : { ...prevSelectedChat, messages: mensajes };
          });
        });
        }; // fin de subscribeToBot

        // Suscribir a los bots accesibles ya cargados. Si aún no hay bots cargados,
        // el efecto reactivo sobre `accessibleBots` suscribirá cuando lleguen.
        subscribeToBotRef.current = subscribeToBot;
        (accessibleBotsRef.current || []).forEach((b) => subscribeToBot(b && b.botId));
      };
      
      stompClient.onStompError = (error) => {
        console.error('❌ Error WebSocket STOMP:', error);
        
        // Detectar errores de autenticación específicos
        if (error.headers && error.headers.message) {
          const errorMessage = error.headers.message;
          console.error('🔐 Error de autenticación WebSocket:', errorMessage);
          
          if (errorMessage.includes('JWT_TOKEN_INVALID') || 
              errorMessage.includes('JWT_VALIDATION_ERROR') ||
              errorMessage.includes('MISSING_AUTH_HEADER')) {
            
            console.warn('🚨 JWT inválido para WebSocket, intentando renovar token...');
            
            // Intentar renovar el token automáticamente
            attemptTokenRefresh().then((resultado) => {
              if (resultado === 'nuevo') {
                console.log('✅ Sesión renovada, reintentando conexión WebSocket...');
                setTimeout(() => connectWebSocket(), 1000);
              } else {
                console.error('❌ No se pudo renovar la sesión, redirigiendo al login');
                handleAuthenticationFailure();
              }
            });
            return;
          }
        }
        
        handleWebSocketError();
      };
      
      stompClient.onWebSocketError = (error) => {
        console.error('❌ Error WebSocket:', error);
        if (stompClientRef.current !== stompClient) return;
        handleWebSocketError();
      };
      
      stompClient.onWebSocketClose = (event) => {
        console.warn('⚠️ WebSocket cerrado:', event.code, event.reason);
        if (stompClientRef.current !== stompClient) return;
        handleWebSocketError();
      };
      
      // Sin connectHeaders: la cookie httpOnly del handshake autentica el CONNECT.

      // IMPORTANTE: Los callbacks se manejan en onConnect/onStompError definidos arriba
      // Activar la conexión (usará onConnect cuando se conecte)
      stompClientRef.current = stompClient;
      stompClient.activate();
      
    } catch (error) {
      console.error('❌ Error creando conexión WebSocket:', error);
      handleWebSocketError();
    } finally {
      connectingRef.current = false;
    }
  }, [reloadChatsFromBackend, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Función para intentar renovar el token automáticamente.
  // Se invoca cuando el servidor ya ha rechazado la sesión (p.ej. 401 del
  // WebSocket), por lo que forzamos el refresh sin mirar el `exp` local.
  // `forceRefresh` reutiliza la misma promesa single-flight que el resto
  // del sistema (en `api.js`), así que no se disparan múltiples refreshes
  // concurrentes.
  const attemptTokenRefresh = async () => {
    try {
      console.log('🔄 Renovando la cookie de sesión...');
      const renovado = await forceRefresh();
      return renovado ? 'nuevo' : 'ninguno';
    } catch (error) {
      console.error('❌ Error renovando la sesión:', error);
      return 'ninguno';
    }
  };

  // Función para manejar fallos de autenticación
  const handleAuthenticationFailure = () => {
    console.warn('🚨 Fallo de autenticación, limpiando sesión...');
    
    // Limpiar el marcador de sesión (la cookie la caduca el backend)
    clearClientSession();
    
    // Desconectar WebSocket
    if (stompClientRef.current && stompClientRef.current.active) {
      stompClientRef.current.deactivate();
    }
    
    // Redirigir al login
    navigate("/");
    
    // Mostrar mensaje al usuario
    alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
  };

  // OPTIMIZACIÓN: Función para manejar errores y reconexión
  const handleWebSocketError = () => {
    if (reconnectTimeoutRef.current) {
      return;
    }

    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.error('❌ Máximo número de intentos de reconexión alcanzado. WebSocket no disponible.');
      return;
    }
    
    reconnectAttempts.current++;
    
    // MEJORA: Backoff exponencial con delay máximo de 30 segundos
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttempts.current - 1),
      30000 // Máximo 30s
    );
    
    console.log(`🔄 Reintentando conexión en ${delay}ms... (intento ${reconnectAttempts.current}/${maxReconnectAttempts})`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connectWebSocket();
    }, delay);
    
    // MEJORA: Aviso al usuario si falla repetidamente
    if (reconnectAttempts.current % 3 === 0) {
      console.warn("⚠️ No se pudo conectar al servidor. Verifica tu red.");
    }
  };

  // Conecta al WebSocket UNA VEZ al montar el Dashboard.
  //
  // La carga inicial de chats la hace el useEffect que reacciona a
  // `selectedBotId` (más arriba), así garantizamos que siempre se pide con el
  // botId correcto y evitamos que aquí se dispare `loadChats(..., null)`
  // durante el render inicial (antes de que `loadAccessibleBots` haya fijado
  // el bot por defecto) — que era la causa de que aparecieran mezclados chats
  // de bots distintos en la lista de un bot concreto.
  //
  // Tampoco depende de `chatsPagination.pageSize` a propósito: el WebSocket
  // no debe reconectarse cada vez que cambia la paginación.
  useEffect(() => {
    connectionGenerationRef.current += 1;
    connectWebSocket();

    // Copiar referencias a variables locales para el cleanup
    const messagesCache = processedMessages.current;

    return () => {
      console.log("🧹 Desmontando componente, limpiando recursos...");
      connectionGenerationRef.current += 1;
      connectingRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const client = stompClientRef.current;
      stompClientRef.current = null;
      if (client && typeof client.deactivate === 'function') {
        client.deactivate(() => {
          console.log("🔌 WebSocket desconectado");
        });
      }

      if (messagesCache) {
        messagesCache.clear();
      }

      reconnectAttempts.current = 0;
      console.log("🧹 Recursos limpiados completamente");
    };
  }, [connectWebSocket]);

  // Mantiene accessibleBotsRef sincronizado para poder leer los bots accesibles
  // dentro de la closure de onConnect sin re-crear la conexión.
  useEffect(() => {
    accessibleBotsRef.current = accessibleBots;
  }, [accessibleBots]);

  // Re-suscribe a los topics por tenant cuando cambian los bots accesibles y ya hay
  // una conexión WebSocket activa (p. ej. los bots se cargan después de conectar).
  useEffect(() => {
    const client = stompClientRef.current;
    if (client && client.connected && subscribeToBotRef.current) {
      accessibleBots.forEach((b) => subscribeToBotRef.current(b && b.botId));
    }
  }, [accessibleBots]);
}

export default useStompConnection;
